import type { PointSystem, Score } from '../model/types.js';

/**
 * Baku Acceleration Method (FIDE C.04.7, applied 1 February 2026).
 *
 * Before round 1 the ranked list is split into GA, the first half rounded
 * up to an even number (2 * ceil(N / 4)), and GB. The accelerated rounds
 * are the first half (rounded up) of the rounds. GA players get virtual
 * points equal to a win before each of the first half (rounded up) of the
 * accelerated rounds, and half that for the remaining accelerated rounds.
 * Virtual points count for pairing only (scoregroups, order, board order).
 * Requires a win to be worth two draws.
 */
export interface BakuPlan {
  readonly gaSize: number;
  readonly acceleratedRounds: number;
  readonly fullRounds: number;
}

export function bakuPlan(players: number, rounds: number): BakuPlan {
  const gaSize = 2 * Math.ceil(players / 4);
  const acceleratedRounds = Math.ceil(rounds / 2);
  const fullRounds = Math.ceil(acceleratedRounds / 2);
  return { gaSize, acceleratedRounds, fullRounds };
}

/** Virtual points (tenths) for a player with pairing number `tpn` in `round`. */
export function bakuPoints(plan: BakuPlan, tpn: number, round: number, ps: PointSystem): Score {
  if (tpn > plan.gaSize || round > plan.acceleratedRounds) return 0;
  if (ps.win !== 2 * ps.draw) throw new Error('Baku acceleration needs a win worth two draws');
  return round <= plan.fullRounds ? ps.win : ps.win / 2;
}

/** The TRF26 250 records describing a Baku plan. */
export function bakuTrfRecords(plan: BakuPlan, ps: PointSystem) {
  const recs = [
    {
      matchPoints: 0,
      points: ps.win,
      firstRound: 1,
      lastRound: plan.fullRounds,
      firstId: 1,
      lastId: plan.gaSize,
    },
  ];
  if (plan.acceleratedRounds > plan.fullRounds) {
    recs.push({
      matchPoints: 0,
      points: ps.win / 2,
      firstRound: plan.fullRounds + 1,
      lastRound: plan.acceleratedRounds,
      firstId: 1,
      lastId: plan.gaSize,
    });
  }
  return recs;
}
